import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { fieldControlStyle } from "../utils/lineItemFieldErrors";
import {
  getErpAuthState,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import {
  LockIconGrey,
  LockIconSizeOnButton,
  LockIconSizeTable,
  LockIconWhiteOnButton,
} from "../utils/LockIconforPermission";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import Pagination from "./Pagination";
import { hrmsApi, hrmsMultipart } from "../utils/hrmsApi";
import { formatDateToDDMMYYYY } from "../pages/const";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";

const HOLIDAY_LOCK_ICON = "solar:lock-bold";

const HOLIDAY_NO_ACCESS_MSG =
  "You do not have permission to view or edit holidays.";

const HOLIDAY_EDIT_ONLY_MSG =
  "You don't have permission to view the holidays list.";

function HolidayNoAccessMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={HOLIDAY_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{HOLIDAY_NO_ACCESS_MSG}</span>
    </span>
  );
}

function HolidayViewLockedMessage() {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2 flex-wrap">
      <Icon
        icon={HOLIDAY_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
      <span>{HOLIDAY_EDIT_ONLY_MSG}</span>
    </span>
  );
}

function HolidayEditLockedHint({ title }) {
  return (
    <span
      className="d-inline-flex align-items-center gap-1"
      title={title || "No permission to edit holidays (HRMS_HOLIDAY_EDIT)."}
      role="img"
      aria-label="No permission to edit holidays"
    >
      <Icon
        icon={HOLIDAY_LOCK_ICON}
        width={LockIconSizeTable}
        height={LockIconSizeTable}
        className="flex-shrink-0"
        style={{ color: LockIconGrey }}
        aria-hidden
      />
    </span>
  );
}

function HolidayLockOnButton({ onSolidButton = false }) {
  return (
    <Icon
      icon={HOLIDAY_LOCK_ICON}
      className="me-1"
      width={LockIconSizeOnButton}
      height={LockIconSizeOnButton}
      style={{
        color: onSolidButton ? LockIconWhiteOnButton : LockIconGrey,
      }}
      aria-hidden
    />
  );
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const HRMS_YEAR_MIN = 2000;
const HRMS_YEAR_MAX = new Date().getFullYear() + 20;
const HRMS_YEARS_PER_PAGE = 12;

function clampYear(y) {
  return Math.min(HRMS_YEAR_MAX, Math.max(HRMS_YEAR_MIN, y));
}

function alignYearWindowStart(selectedYear) {
  const y = clampYear(selectedYear);
  const maxStart = HRMS_YEAR_MAX - HRMS_YEARS_PER_PAGE + 1;
  let start = y - 5;
  if (start < HRMS_YEAR_MIN) start = HRMS_YEAR_MIN;
  if (start > maxStart) start = maxStart;
  return start;
}

function HrmsYearPicker({ year, onChange, disabled, buttonTableStyle }) {
  const [open, setOpen] = useState(false);
  const [windowStart, setWindowStart] = useState(() =>
    alignYearWindowStart(year),
  );
  const ref = useRef(null);

  useEffect(() => {
    if (open) setWindowStart(alignYearWindowStart(year));
  }, [open, year]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const visibleYears = useMemo(() => {
    const list = [];
    for (
      let y = windowStart;
      y < windowStart + HRMS_YEARS_PER_PAGE && y <= HRMS_YEAR_MAX;
      y += 1
    ) {
      if (y >= HRMS_YEAR_MIN) list.push(y);
    }
    return list;
  }, [windowStart]);

  const rangeLabel =
    visibleYears.length > 0
      ? `${visibleYears[0]} – ${visibleYears[visibleYears.length - 1]}`
      : String(year);

  const shiftWindow = (delta) => {
    setWindowStart((prev) => {
      const next = prev + delta;
      const maxStart = HRMS_YEAR_MAX - HRMS_YEARS_PER_PAGE + 1;
      return Math.max(HRMS_YEAR_MIN, Math.min(next, maxStart));
    });
  };

  const pickYear = (y) => {
    onChange(clampYear(y));
    setOpen(false);
  };

  const square = buttonTableStyle === "square";
  const controlRadius = square ? 0 : 8;

  return (
    <div
      ref={ref}
      className="position-relative flex-shrink-0"
      style={{ width: 220, minWidth: 220 }}
    >
      <button
        type="button"
        className="d-flex align-items-center justify-content-between px-3 bg-white w-100"
        style={{
          minHeight: 44,
          border: "1px solid #e0e6eb",
          borderRadius: controlRadius,
          color: "#212529",
          boxShadow: "none",
        }}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-label="Select year"
        aria-expanded={open}
      >
        <span className="text-start">{year}</span>
        <Icon
          icon="solar:alt-arrow-down-bold"
          width={14}
          className="ms-3 flex-shrink-0"
        />
      </button>
      {open && (
        <div
          className={`border bg-white shadow-sm p-3 position-absolute top-100 start-0 mt-1 ${square ? "rounded-0" : "rounded"
            }`}
          style={{ zIndex: 1050, minWidth: 280 }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <button
              type="button"
              className={`btn btn-sm btn-outline-secondary ${square ? "rounded-0" : ""}`}
              disabled={windowStart <= HRMS_YEAR_MIN}
              onClick={() => shiftWindow(-HRMS_YEARS_PER_PAGE)}
              aria-label="Previous years"
            >
              <Icon icon="solar:alt-arrow-left-bold" width={16} />
            </button>
            <span className="fw-semibold small text-muted">{rangeLabel}</span>
            <button
              type="button"
              className={`btn btn-sm btn-outline-secondary ${square ? "rounded-0" : ""}`}
              disabled={windowStart + HRMS_YEARS_PER_PAGE > HRMS_YEAR_MAX}
              onClick={() => shiftWindow(HRMS_YEARS_PER_PAGE)}
              aria-label="Next years"
            >
              <Icon icon="solar:alt-arrow-right-bold" width={16} />
            </button>
          </div>
          <div className="row row-cols-4 g-2">
            {visibleYears.map((y) => (
              <div key={y} className="col">
                <button
                  type="button"
                  className={`btn btn-sm w-100 ${y === year ? "btn-primary" : "btn-outline-secondary"
                    } ${square ? "rounded-0" : ""}`}
                  onClick={() => pickYear(y)}
                >
                  {y}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const HOLIDAY_TEMPLATE_FILENAME = "Holiday Template.csv";
const HOLIDAY_IMPORT_ACCEPT =
  ".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";

function resolveTenantId() {
  const u = getErpAuthState().user || {};
  const n = Number(u.tenant_id ?? u.tenantId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function withTenantId(payload, tenantId) {
  if (tenantId) return { ...payload, tenant_id: tenantId };
  return payload;
}

function downloadHolidayTemplate() {
  const header = ["date", "name"];
  const csv = `${header.join(",")}\r\n`;
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = HOLIDAY_TEMPLATE_FILENAME;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function HolidaysTab() {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canView = hasPermission(PERMISSIONS.HRMS_HOLIDAY_VIEW);
  const canEdit = hasPermission(PERMISSIONS.HRMS_HOLIDAY_EDIT);
  const hasAnyAccess = canView || canEdit;
  const editLockedTitle = "No permission to edit holidays (HRMS_HOLIDAY_EDIT).";

  const tenantId = useMemo(() => resolveTenantId(), []);

  const [year, setYear] = useState(() => new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRows([]);
  }, [year]);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [addMode, setAddMode] = useState("manual");
  const [formDate, setFormDate] = useState("");
  const [formName, setFormName] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const csvInputRef = useRef(null);

  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(async () => {
    if (!hasAnyAccess || !canView) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await hrmsApi(
        "holiday_list",
        withTenantId({ year: Number(year) }, tenantId),
      );
      if (isErpApiSuccess(res) && res.data) {
        const list = Array.isArray(res.data.holidays) ? res.data.holidays : [];
        setRows(list);
      } else {
        setRows([]);
        if (res?.success === false && res?.message) {
          toast.error(res.message);
        }
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load holidays.");
    } finally {
      setLoading(false);
    }
  }, [year, canView, hasAnyAccess, tenantId]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [year, searchQuery]);

  const resetAddForm = () => {
    setAddMode("manual");
    setFormDate("");
    setFormName("");
    setCsvFile(null);
    if (csvInputRef.current) csvInputRef.current.value = "";
  };

  const openAdd = () => {
    if (!canEdit) return;
    setEditingId(null);
    resetAddForm();
    setShowModal(true);
  };

  const openEdit = (r) => {
    if (!canEdit) return;
    setEditingId(r.id);
    setAddMode("manual");
    const d = String(r.holiday_date || "").split("T")[0];
    setFormDate(d);
    setFormName(String(r.name || ""));
    setCsvFile(null);
    if (csvInputRef.current) csvInputRef.current.value = "";
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingId(null);
    resetAddForm();
  };

  const saveHoliday = async ({ holiday_date, name, id }) => {
    const body = withTenantId(
      {
        holiday_date: String(holiday_date).trim(),
        name: String(name).trim(),
      },
      tenantId,
    );
    if (id != null) body.id = Number(id);
    return hrmsApi("holiday_save", body);
  };

  const submitManual = async () => {
    const next = {};
    const messages = [];
    if (!DATE_RE.test(String(formDate || "").trim())) {
      next.date = true;
      messages.push("Enter a valid holiday date (yyyy-mm-dd).");
    }
    const name = String(formName || "").trim();
    if (!name) {
      next.name = true;
      messages.push("Enter holiday name.");
    }
    setFieldErrors(next);
    if (messages.length) {
      toast.error(messages[0]);
      return;
    }
    setSaving(true);
    try {
      const res = await saveHoliday({
        holiday_date: formDate,
        name,
        id: editingId,
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            editingId != null ? "Holiday updated." : "Holiday added.",
          ),
        );
        closeModal();
        await fetchList();
      } else {
        toast.error(res?.message || "Could not save holiday.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not save holiday.");
    } finally {
      setSaving(false);
    }
  };

  const submitCsvUpload = async () => {
    if (!csvFile) {
      setFieldErrors({ file: true });
      toast.error("Select a file to upload.");
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("type", "holiday_import_excel");
      if (tenantId) fd.append("tenant_id", String(tenantId));
      fd.append("file", csvFile);
      const res = await hrmsMultipart(fd);
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(res, "Holidays imported successfully."),
        );
        closeModal();
        await fetchList();
      } else {
        toast.error(res?.message || "Could not import holidays.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not import holidays.");
    } finally {
      setSaving(false);
    }
  };

  const submitModal = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      toast.error("You do not have permission to edit holidays.");
      return;
    }
    if (editingId != null || addMode === "manual") {
      await submitManual();
    } else {
      await submitCsvUpload();
    }
  };

  const onCsvChange = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) {
      setCsvFile(null);
      return;
    }
    setFieldErrors((prev) => {
      if (!prev.file) return prev;
      const next = { ...prev };
      delete next.file;
      return next;
    });
    const name = String(file.name || "").toLowerCase();
    if (!/\.(csv|xlsx|xls)$/.test(name)) {
      toast.error("Only .csv, .xlsx, or .xls files are allowed.");
      ev.target.value = "";
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
  };

  const confirmDelete = async () => {
    if (!canEdit || !deleteRow) return;
    const id = deleteRow.id != null ? Number(deleteRow.id) : null;
    const holidayDate = String(deleteRow.holiday_date || "")
      .split("T")[0]
      .trim();
    if (!id && !DATE_RE.test(holidayDate)) return;
    setDeleting(true);
    try {
      const body = withTenantId({}, tenantId);
      if (id) body.id = id;
      else body.holiday_date = holidayDate;
      const res = await hrmsApi("holiday_delete", body);
      if (isErpApiSuccess(res)) {
        toast.success(hrmsSuccessMessage(res, "Holiday deleted."));
        setDeleteRow(null);
        await fetchList();
      } else {
        toast.error(res?.message || "Could not delete.");
      }
    } catch (err) {
      toast.error(getErpRequestErrorMessage(err, "Could not delete."));
    } finally {
      setDeleting(false);
    }
  };

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) =>
        String(a.holiday_date || "").localeCompare(
          String(b.holiday_date || ""),
        ),
      ),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((r) => {
      const dateStr =
        formatDateToDDMMYYYY(r.holiday_date) ||
        String(r.holiday_date || "").split("T")[0];
      const name = String(r.holiday_name ?? r.name ?? "");
      return `${dateStr} ${name}`.toLowerCase().includes(q);
    });
  }, [sortedRows, searchQuery]);

  const displayRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const showActionsColumn = hasAnyAccess;
  const colCount = showActionsColumn ? 3 : 2;
  const isAddModal = editingId == null;

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
          <div className="d-flex align-items-center gap-3 flex-nowrap">
            <HrmsYearPicker
              year={year}
              disabled={!hasAnyAccess}
              buttonTableStyle={buttonTableStyle}
              onChange={setYear}
            />
            <input
              type="text"
              className={`form-control flex-shrink-0 ${buttonClass}`}
              style={{ width: 260 }}
              placeholder="Search..."
              value={searchQuery}
              onChange={(ev) => {
                setSearchQuery(ev.target.value);
                setCurrentPage(1);
              }}
              disabled={!hasAnyAccess || !canView}
              aria-label="Search holidays"
            />
          </div>
          {hasAnyAccess ? (
            <Button
              type="button"
              className={`btn btn-success flex-shrink-0 ${buttonClass}`}
              disabled={!canEdit}
              title={!canEdit ? editLockedTitle : undefined}
              onClick={canEdit ? openAdd : undefined}
            >
              {!canEdit ? <HolidayLockOnButton onSolidButton /> : null}
              Add Holiday
            </Button>
          ) : null}
        </div>
      </div>

      <div className="col-12">
        <div
          className={`table-responsive border position-relative ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          {!hasAnyAccess ? (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-90"
              style={{ zIndex: 5 }}
            >
              <HolidayNoAccessMessage />
            </div>
          ) : null}
          <table
            className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
              }`}
          >
            <thead className="text-dark fs-4">
              <tr>
                <th>Date</th>
                <th>Holiday name</th>
                {showActionsColumn ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {!hasAnyAccess ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    <HolidayNoAccessMessage />
                  </td>
                </tr>
              ) : !canView ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    <HolidayViewLockedMessage />
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    Loading…
                  </td>
                </tr>
              ) : filteredRows.length > 0 ? (
                displayRows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDateToDDMMYYYY(r.holiday_date)}</td>
                    <td>{String(r.name || "—")}</td>
                    {showActionsColumn ? (
                      <td>
                        {canEdit ? (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              className={`me-1 ${buttonClass}`}
                              onClick={() => openEdit(r)}
                            >
                              <Icon icon="solar:pen-bold" width={16} />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              className={buttonClass}
                              onClick={() => setDeleteRow(r)}
                            >
                              <Icon
                                icon="solar:trash-bin-minimalistic-bold"
                                width={16}
                              />
                            </Button>
                          </>
                        ) : (
                          <HolidayEditLockedHint title={editLockedTitle} />
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))
              ) : !loading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="text-center py-5 text-muted"
                  >
                    {searchQuery.trim()
                      ? "No holidays match your search."
                      : "No holidays for this year."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {canView && filteredRows.length > 0 ? (
        <div className="col-12">
          <Pagination
            totalItems={filteredRows.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => {
              setItemsPerPage(n);
              setCurrentPage(1);
            }}
          />
        </div>
      ) : null}

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {editingId ? "Edit Holiday" : "Add Holiday"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitModal}>
          <Modal.Body>
            {isAddModal && (
              <div className="mb-3">
                <Form.Label className="fw-semibold">Add method  </Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  <Form.Check
                    type="radio"
                    id="holiday-add-manual"
                    name="holidayAddMode"
                    label="Manual entry"
                    checked={addMode === "manual"}
                    disabled={saving}
                    onChange={() => {
                      setAddMode("manual");
                      setCsvFile(null);
                      if (csvInputRef.current) csvInputRef.current.value = "";
                    }}
                  />
                  <Form.Check
                    type="radio"
                    id="holiday-add-upload"
                    name="holidayAddMode"
                    label="Upload CSV"
                    checked={addMode === "upload"}
                    disabled={saving}
                    onChange={() => {
                      setAddMode("upload");
                      setFormDate("");
                      setFormName("");
                    }}
                  />
                </div>
              </div>
            )}

            {(editingId != null || addMode === "manual") && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Date </Form.Label>
                  <Form.Control
                    type="date"
                    value={formDate}
                    onChange={(ev) => setFormDate(ev.target.value)}
                    required={editingId != null || addMode === "manual"}
                    disabled={saving}
                    style={fieldControlStyle(fieldErrors.date)}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Holiday name </Form.Label>
                  <Form.Control
                    value={formName}
                    onChange={(ev) => setFormName(ev.target.value)}
                    placeholder="Holiday name"
                    required={editingId != null || addMode === "manual"}
                    disabled={saving}
                    style={fieldControlStyle(fieldErrors.name)}
                  />
                </Form.Group>
              </>
            )}

            {isAddModal && addMode === "upload" && (
              <Form.Group className="mb-0">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <Form.Label className="mb-0">Excel / CSV file</Form.Label>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    className={buttonClass}
                    disabled={saving}
                    onClick={downloadHolidayTemplate}
                  >
                    <Icon
                      icon="solar:download-minimalistic-linear"
                      className="me-1"
                      width={16}
                    />
                    Download Template
                  </Button>
                </div>
                <Form.Control
                  ref={csvInputRef}
                  type="file"
                  accept={HOLIDAY_IMPORT_ACCEPT}
                  disabled={saving}
                  onChange={onCsvChange}
                  style={fieldControlStyle(fieldErrors.file)}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="submit"
              variant="success"
              className={buttonClass}
              disabled={saving}
            >
              {saving
                ? "Saving…"
                : isAddModal && addMode === "upload"
                  ? "Upload & save"
                  : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={!!deleteRow}
        onHide={() => !deleting && setDeleteRow(null)}
        centered
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete Holiday</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this holiday?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            className={buttonClass}
            disabled={deleting}
            onClick={confirmDelete}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
